import React, { useState, useEffect, useCallback } from "react";
import { Box, Grid, Typography, Pagination, Stack } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";
import PackageUnitCard from "../components/packageUnits/PackageUnitCard";
import CreatePackageUnitModal from "../components/packageUnits/CreatePackageUnitModal";
import UpdatePackageUnitModal from "../components/packageUnits/UpdatePackageUnitModal";
import AppCircularProgress from "../components/common/AppCircularProgress";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getPackageUnits,
  deletePackageUnit,
  resetPackageUnitServerStatus,
} from "../redux/actions/packageUnitActions";
import PageError from "../components/common/PageError";
import { PackageUnitResponseDto } from "../shared/dtos/packageUnit/PackageUnitResponseDto";

const PackageUnitsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  // Redux State
  const {
    items: packageUnits,
    pagination,
    loadingFetchAll: loadingPackageUnits,
    errorFetchAll: errorLoadingPackageUnits,
    serverStatus,
    loadingDelete: loadingPackageUnitDelete,
    loadingUpdate: loadingPackageUnitUpdate,
    loadingCreate: loadingPackageUnitCreate,
    errorCreate: errorCreatingPackageUnit,
    errorUpdate: errorUpdatingPackageUnit,
  } = useAppSelector((state) => state.packageUnit);

  // Local State
  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(12);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedUnit, setSelectedUnit] =
    useState<PackageUnitResponseDto | null>(null);

  // Permissions
  const canCreate = hasPermission("package_unit:create");
  const canUpdate = hasPermission("package_unit:update");
  const canDelete = hasPermission("package_unit:delete");

  // Fetch Logic
  const fetchUnits = useCallback(() => {
    dispatch(getPackageUnits({ page, size: limit, search: debouncedSearch }));
  }, [dispatch, page, limit, debouncedSearch]);

  useEffect(() => {
    fetchUnits();
  }, [fetchUnits]);

  // Reset to page 1 on search
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  // Handlers
  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number,
  ): void => setPage(value);

  const handleOpenCreate = (): void => setIsCreateModalOpen(true);
  const handleCloseCreate = useCallback(
    () => !loadingPackageUnitCreate && setIsCreateModalOpen(false),
    [loadingPackageUnitCreate],
  );

  const handleOpenUpdate = (unit: PackageUnitResponseDto): void => {
    setSelectedUnit(unit);
    setIsUpdateModalOpen(true);
  };
  const handleCloseUpdate = useCallback(() => {
    if (!loadingPackageUnitUpdate) {
      setIsUpdateModalOpen(false);
      setSelectedUnit(null);
    }
  }, [loadingPackageUnitUpdate]);

  const handleOpenDelete = (unit: PackageUnitResponseDto): void => {
    setSelectedUnit(unit);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDelete = useCallback(() => {
    if (!loadingPackageUnitDelete) {
      setIsDeleteModalOpen(false);
      setSelectedUnit(null);
    }
  }, [loadingPackageUnitDelete]);

  const handleDeleteConfirm = (): void => {
    if (selectedUnit) dispatch(deletePackageUnit(selectedUnit.id));
  };

  // Auto Close on Success
  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;
    if (operation === "create" && (status === 201 || status === 200))
      handleCloseCreate();
    if (operation === "update" && status === 200) handleCloseUpdate();
    if (operation === "delete" && status === 200) handleCloseDelete();
    dispatch(resetPackageUnitServerStatus());
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  const renderContent = (): React.JSX.Element => {
    if (loadingPackageUnits) return <AppCircularProgress />;

    if (errorLoadingPackageUnits)
      return (
        <PageError error={errorLoadingPackageUnits} onRetry={fetchUnits} />
      );

    if (!packageUnits || packageUnits.length === 0)
      return (
        <Box sx={{ textAlign: "center", mt: 5 }}>
          <Typography>No package units found.</Typography>
        </Box>
      );

    return (
      <>
        <Grid container spacing={3}>
          {packageUnits.map((unit) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={unit.id}>
              <PackageUnitCard
                unit={unit}
                onEdit={canUpdate ? () => handleOpenUpdate(unit) : undefined}
                onDelete={canDelete ? () => handleOpenDelete(unit) : undefined}
              />
            </Grid>
          ))}
        </Grid>

        {pagination && pagination.totalPages! > 1 && (
          <Stack sx={{ alignItems: "center", mt: 4 }}>
            <Pagination
              count={pagination.totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Stack>
        )}
      </>
    );
  };

  return (
    <PageContainer
      title="Package Units"
      subtitle="Manage packaging configurations (e.g., Boxes, Cartons, Pallets) for inventory."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleOpenCreate}
          >
            Add Package Unit
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search packages by name or code..."
      />

      {renderContent()}

      {isCreateModalOpen && (
        <CreatePackageUnitModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingPackageUnitCreate}
          error={errorCreatingPackageUnit}
        />
      )}

      {isUpdateModalOpen && selectedUnit && (
        <UpdatePackageUnitModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          unit={selectedUnit}
          loading={loadingPackageUnitUpdate}
          error={errorUpdatingPackageUnit}
        />
      )}

      {isDeleteModalOpen && selectedUnit && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Package Unit"
          description={`Are you sure you want to delete "${selectedUnit.name}"? This action may affect products currently using this package unit.`}
          loading={loadingPackageUnitDelete}
          severity="error"
        />
      )}
    </PageContainer>
  );
};

export default PackageUnitsPage;
